import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  contactStylist as contactStylistAction,
  resetContactForm as resetContactFormAction,
} from 'actions/BookingActions';
import { config } from 'config';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { getContactForm } from 'selectors/contact';
import validator from 'validator';
import * as yup from 'yup';

import Button from 'components/Button';
import { InputField } from 'components/InputField/InputField';
import { RecaptchaDisclaimer } from 'components/RecaptchaDisclaimer/RecaptchaDisclaimer';

import { useBusiness } from 'hooks/business/useBusiness';
import { useAppContext } from 'hooks/context/useAppContext';

import { ContactInfo } from './types';

export enum ContactFormState {
  SENT = 'sent',
  ERROR = 'error',
  DEFAULT = 'default',
}

type UseContactPageParams = {
  styles: {
    input?: string;
    textarea?: string;
    button?: string;
    error?: string;
    title?: string;
    subtitle?: string;
    recaptchaDisclaimer?: string;
  };
};

export const useContactPage = ({ styles }: UseContactPageParams) => {
  const {
    publicUser: { slug, users },
  } = useAppContext();

  const { isMultiUser } = useBusiness();
  const contactForm = useSelector(getContactForm);

  const [showingError, setShowingError] = React.useState(false);
  const [formState, setFormState] = React.useState<ContactFormState>(
    ContactFormState.DEFAULT,
  );

  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  // Dispatch handlers
  const contactStylist = (values: ContactInfo, slug: string) =>
    dispatch(contactStylistAction(values, slug));
  const resetContactForm = React.useCallback(
    () => dispatch(resetContactFormAction()),
    [dispatch],
  );

  React.useEffect(() => {
    // Reset the form on unmount. Necessary?! Because it's Redux?
    return () => {
      resetContactForm();
    };
  }, [resetContactForm]);

  const getRecaptchaToken = async (action: string) => {
    const recaptchaSiteKey = config.recaptchaSiteKey;

    return new Promise<string>((resolve) => {
      window.grecaptcha.ready(() => {
        void window.grecaptcha
          .execute(recaptchaSiteKey, { action })
          .then((token: string) => {
            resolve(token);
          });
      });
    });
  };

  const onSubmit = async (values: ContactInfo) => {
    values.recaptcha_token = await getRecaptchaToken('checkContactForm');
    void contactStylist(values, slug);
  };

  React.useEffect(() => {
    if (showingError) {
      setTimeout(() => {
        setShowingError(false);
      }, 500);
    }
  }, [showingError]);

  React.useEffect(() => {
    if (contactForm) {
      switch (contactForm.last_action) {
        case 'contact_success':
          setFormState(ContactFormState.SENT);
          break;
        case 'contact_error':
          setFormState(ContactFormState.ERROR);
          break;
        default:
          setFormState(ContactFormState.DEFAULT);
      }
    }
  }, [contactForm]);

  const formTitle = `Ask ${
    isMultiUser ? 'us' : users?.length > 0 ? users[0]?.full_name : 'me'
  } a Question`;

  const formSubtitle = `${
    isMultiUser ? 'We' : users?.length > 0 ? users[0]?.full_name : 'I'
  } will receive an email with your message`;

  const contactInfoSchema = yup
    .object({
      name: yup.string().required('Please enter Name'),
      email: yup
        .string()
        .required('Please enter Email address')
        .test('is-email', 'Please enter a valid email address', (value) =>
          validator.isEmail(value),
        ),
      message: yup.string().required('Please enter Message'),
    })
    .required();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ContactInfo>({
    defaultValues: {
      name: '',
      email: '',
      message: '',
    } as ContactInfo,
    resolver: yupResolver(contactInfoSchema),
    mode: 'onBlur',
  });

  const renderFormElements = () => (
    <>
      <InputField
        id="contact-name"
        label="Name"
        name="name"
        register={register('name')}
        error={errors.name?.message}
        required
        className={styles.input}
      />
      <InputField
        id="contact-email"
        label="Email"
        name="email"
        type="email"
        register={register('email')}
        error={errors.email?.message}
        required
        className={styles.input}
      />
      <InputField
        id="contact-message"
        label="Message"
        type="textarea"
        name="message"
        register={register('message')}
        error={errors.message?.message}
        required
        className={styles.textarea}
      />
      <div className={styles.subtitle}>{formSubtitle}</div>
      <RecaptchaDisclaimer className={styles.recaptchaDisclaimer} />
      <Button
        className={styles.button}
        type="submit"
        loading={contactForm.loading}
        disabled={contactForm.loading}
      >
        Send
      </Button>
    </>
  );

  return {
    formTitle,
    formState,
    renderFormElements,
    onSubmit: (event: React.FormEvent<HTMLFormElement>) =>
      void handleSubmit(onSubmit)(event),
  };
};
